// setTriggerPrivateInviteCountdown.js
'use strict';
import {
  MERGE_LIVE_STREAM_DATA,
  MERGE_OPERATION_DATA,
  REMOVE_OPERATION_DATA,
} from '../ActionTypes.js';
import createTriggerPrivateRequest from '../action/createTriggerPrivateRequest.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

/**
 * Set (or clear) the client-side pre-send invite countdown, and own the timer
 * that actually sends the invite at expiry. The timer lives in this action
 * (keyed by streamId in operation state) instead of a display component, so
 * unmounting the panel never drops or implicitly cancels the send.
 * @kind action
 * @param {object} params
 * @param {string} params.streamId - stream id.
 * @param {?number} [params.seconds] - countdown length in seconds; null to clear / cancel.
 * @param {?string} [params.triggerType] - LivestreamTriggerType the countdown is for.
 * @returns {object} The dispatched action.
 */
const setTriggerPrivateInviteCountdown =
  ({ streamId, seconds = null, triggerType = null } = {}) =>
  (dispatch, getState) => {
    if (!streamId) {
      return dispatch({ type: '' });
    }
    const timerSelectPath = [
      'timer',
      'livestream',
      streamId,
      'triggerPrivateInviteSend',
    ];
    const existedTimerId = getOperationData(
      getState(),
      timerSelectPath,
      'timerId'
    );
    if (existedTimerId) {
      clearTimeout(existedTimerId);
      dispatch({
        type: REMOVE_OPERATION_DATA,
        payload: {
          selectPath: timerSelectPath,
        },
      });
    }

    const inviteCountdownExp =
      seconds == null
        ? null
        : getCurrentUnixTimestamp({
            offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
          }) + seconds;

    if (inviteCountdownExp != null) {
      const timerId = setTimeout(() => {
        dispatch({
          type: REMOVE_OPERATION_DATA,
          payload: {
            selectPath: timerSelectPath,
          },
        });
        // Fire-time guard: the countdown may have been cancelled after
        // scheduling; a cancelled invite must never be sent.
        const currentInviteCountdownExp = getLiveStreamData(
          getState(),
          [streamId, 'triggerPrivateInvite'],
          'inviteCountdownExp'
        );
        if (currentInviteCountdownExp == null) {
          return;
        }
        dispatch(createTriggerPrivateRequest({ streamId, triggerType }));
      }, seconds * 1000);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: timerSelectPath,
          data: {
            timerId,
            unixTimestamp: inviteCountdownExp,
          },
        },
      });
    }

    return dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'triggerPrivateInvite'],
        data: {
          inviteCountdownExp,
          triggerType: inviteCountdownExp == null ? null : triggerType,
          isInviteSubmitted: false,
        },
      },
    });
  };

export default setTriggerPrivateInviteCountdown;
